import EventBus from '@/components/mixins/event-bus-mixin'
import AuthMixin from '@/components/mixins/auth-mixin'
import { proxyException, EitherResponse } from '@/utils/communication'
import { DatatransLightbox, DatatransRedirect } from '@/utils/datatrans-payment'
import DialogPopup from '@/components/common/dialog-popup/dialog-popup-tmpl'
import { EventTypes, CLOSE_DIALOG } from '@/components/common/dialog-popup/dialog-popup'
import DialogPopupPage from '@/components/common/dialog-popup/dialog-popup-page/dialog-popup-page-tmpl'
import PlanUpgradeOverview from './plan-upgrade-overview/plan-upgrade-overview-tmpl'
import PlanUpgradeBillingDetails from './plan-upgrade-billing-details/plan-upgrade-billing-details-tmpl'
import Payment from './plan-upgrade-payment/plan-upgrade-payment-tmpl'
import { Plan } from './upgrade-plan-builder'
import _ from 'lodash'
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth';
import { useGroupStore } from '@base-shared/stores/group';

import {
  PLAN_UPGRADE_STEPS,
  PROGRESS_SCENARIOS,
  PROGRESS_SCENARIO_OPTIONS,
  CLOSE_PLAN_UPGRADE_EVENT
} from './plan-upgrade-common'

import {
  submitOverviewForm,
  submitBillingDetailsForm,
  handlePaymentForm
} from './plan-form-handlers'

export const FORM_HANDLERS = {
  submitOverviewForm,
  submitBillingDetailsForm,
  handlePaymentForm
}

export default {
  name: 'plan-upgrade',
  inject: [
    '$currentUser',
    '$utils',
    '$api',
    '$signedInUserInfo'
  ],
  components: {
    DialogPopup,
    DialogPopupPage,
    PlanUpgradeOverview,
    PlanUpgradeBillingDetails,
    Payment
  },
  props: {
    isOpened: {
      type: Boolean,
      default: true
    }
  },
  mixins: [AuthMixin],
  data () {
    const handlers = _.fromPairs(_.map(FORM_HANDLERS, (fn, key) => [key, fn.bind(this)]))
    return {
      handlers,
      steps: PLAN_UPGRADE_STEPS,
      progress: PROGRESS_SCENARIOS,
      progressScenarioId: PROGRESS_SCENARIO_OPTIONS.UPGRADE,
      activePageId: PLAN_UPGRADE_STEPS.STEP_OVERVIEW,
      datatransLightbox: new DatatransLightbox(Datatrans, CONFIG),
      datatransRedirect: new DatatransRedirect(),
      plan: new Plan(),
      userInfo: null,
      payment: {},
      isDatatransDialogOpen: false,
      datatranseMessageEventListener: null
    }
  },
  watch: {
    isOpened: {
      immediate: true,
      async handler (newVal, oldVal) {
        if (newVal && oldVal !== newVal) {
          this.progressScenarioId = PROGRESS_SCENARIO_OPTIONS.UPGRADE
          await this.resetPlan()
        }
      }
    }
  },
  computed: {
    billingDetails () {
      return !_.isEmpty(this.plan) ? this.plan?.getBillingDetails() ?? {} : {}
    },
    hasPayment () {
      return !_.isEmpty(this.payment)
    },
    isPaymentNextButtonVisible () {
      return !!this.payment.success
    },
    paymentNextButtonTitle () {
      return this.payment.success ? this.$t('planUpgrade.navFinish') : ''
    },
    isPaymentFailedButtonVisible () {
      return !!this.payment.failed
    },
    paymentFailButtonTitle () {
      return this.payment.failed ? this.$t('planUpgrade.tryAgain') : ''
    },
    paymentPrevButtonTitle () {
      return this.payment.failed ? this.$t('planUpgrade.BillingDetails') : ''
    }
  },
  setup () {
    // Expose the user store to the Options API context.
    return { groupStore: useGroupStore() };
  },
  methods: {
    async resetPlan () {
      const userInfo = await this.getUserInfo()

      if (!_.isEmpty(userInfo)) {
        this.plan.setUserInfo(userInfo)
        this.userInfo = this.plan.getUserInfo()
        await this.reinitializePlan()
      }
    },
    async getUserInfo () {
      this.$utils.mainLoader(true)
      const { ok: response, error } = await proxyException(() => this.$api.get(`${CONFIG.API_BASE_URL}/group/info`))
      this.$utils.mainLoader(false)

      if (error) {
        EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
          type: 'error',
          alert: {
            message: error.response ? error.response?.data.message ?? error.message : this.$t('errors.failedToFetchUserInfo')
          }
        })
        return
      }

      return this.$currentUser.filterUserInfoFromBackend(response.data)
    },
    getCurrentScenarioIndex (currentPageId) {
      if (!this.progressScenarioId) {
        return [null, null]
      }
      const scenario = PROGRESS_SCENARIOS[this.progressScenarioId]
      return [scenario, scenario.findIndex(s => s.id === currentPageId)]
    },
    async commitBillingDetails () {
      const url = `${CONFIG.API_BASE_URL}/group/info`

      const data = { billing_address: this.plan.getBillingDetails() }

      const { ok: response, error } = await proxyException(() => this.$api.put(url, data))

      if (error) {
        return EitherResponse(null, error.response?.data?.message ?? error.message)
      }
      return EitherResponse(response.data)
    },
    async commitNewPlan () {
      this.$utils.mainLoader(true)
      const { ok: response, error } = await proxyException(() => this.$api.put(`${CONFIG.API_BASE_URL}/group/plan`, { plan: 'premium' }))

      if (error) {
        EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
          type: 'error',
          alert: {
            message: error.response ? error.response?.data.message ?? error.message : this.$t('errors.failedToFetchUserInfo')
          }
        })
        this.$utils.mainLoader(false)
        return
      }

      // Update the group store with the new plan information
      // which will trigger the watcher in auth.js plugin
      // to update signedInUserInfo in replacement for the
      // `BIND_USER_INFO_IN_VUE`, `USER_INFO_AVAILABLE_IN_VUE`
      // from EventBus.
      // NOTE: Check if we can use auth-mixin#getUserUserInfoFromBackend
      if (this.groupStore.groupInfo) {
        this.groupStore.groupInfo.plans = response.data.plans
      }

      /**
       * Refresh user session.
       * This will fetch new token from the backend with updated user rights.
       */
      this.refreshUserSession()
      this.$utils.mainLoader(false)
    },
    async showDatatransLightbox () {
      this.$utils.mainLoader(true)
      const redirectUrl = `${window.location.protocol}//${window.location.host}${this.$route.path}?datatransRedirect=1`
      this.datatransLightbox.open(this.$currentUser.getJWToken(), this.plan.getUserInfo(), redirectUrl)
    },
    async informParentToOpenDialog () {
      this.$emit('open-dialog')
    },
    setPaymentAsActivePage () {
      this.activePageId = this.steps.STEP_PAYMENT
      this.informParentToOpenDialog()
    },
    setPaymentData (data = {}) {
      this.payment = data
    },
    /**
     * Show payment page by default if there's url response data
     */
    async handleDatatransResponseData (data) {
      if (_.isEmpty(data)) {
        return
      }

      this.setPaymentAsActivePage()
      this.setPaymentData(data)
      await this.resetPlan()

      if (data.success) {
        this.commitNewPlan()
      }
    },
    handleDatatransOpen () {
      this.$utils.mainLoader(false)
      this.isDatatransDialogOpen = true
    },
    handleDatatransClose () {
      this.isDatatransDialogOpen = false
      this.payment = {}
      this.switchPageAction(this.steps.STEP_BILLING_DETAILS)
    },
    handleDatatransSubmit () {
      this.$utils.mainLoader(true, this.$t('label.loading'), true)
    },
    /**
     * Function to determine what page should go next based on two inputs:
     *   1. $data.progressScenarioId
     *   2. pageId
     * @param {string} currentPageId - page to calculate next scenario ID
     */
    nextPage (currentPageId) {
      const [scenario, index] = this.getCurrentScenarioIndex(currentPageId)
      if (!index || scenario.length <= index + 1) {
        return CLOSE_DIALOG
      }
      // return next page ID
      return scenario[index + 1].id
    },
    prevPage (currentPageId) {
      const [scenario, index] = this.getCurrentScenarioIndex(currentPageId)
      if (!index) {
        return CLOSE_DIALOG
      } else if (index === 0) {
        return scenario[0].id
      }
      return scenario[index - 1 >= 0 ? index - 1 : 0].id
    },
    handleBackButton (currentPage) {
      return async () => await this.switchPageAction(this.prevPage(currentPage))
    },
    handleDialogClose (data) {
      this.payment = {}
      this.progressScenarioId = null
      this.$utils.proxyEvent(
        this,
        EventTypes.DialogClose,
        CLOSE_PLAN_UPGRADE_EVENT,
        data
      )

      if (!_.isEmpty(this.payment)) {
        this.$router.push('/plans')
      }
    },
    async reinitializePlan () {
      const planData = this.plan.getData()
      this.plan = null
      this.plan = new Plan(planData)
    },
    async switchPageAction (page) {
      // propogate all properties to downstream components before switch
      await this.reinitializePlan()
      // I want to make sure that properties are populated BEFORE page switch
      await this.$refs.dialog.switchPageAction(page)
    },
    handlePageChange (pageData) {
      this.activePageId = pageData?.pageId ?? this.activePageId
    },

    initDatatransLightbox () {
      const sessionStage = useCognitoAuthStore().sessionStage ?? CONFIG.GLOBAL_STAGE;
      if (!sessionStage) {
        console.error('Invalid or missing stage:', sessionStage)
        return;
      }

      this.datatransLightbox = new DatatransLightbox(Datatrans, CONFIG, sessionStage)

      if (!_.isEmpty(this.datatransLightbox)) {
        this.datatransLightbox.setOnReady(() => this.handleDatatransOpen())
        this.datatransLightbox.setOnClose(() => this.handleDatatransClose())
        this.datatransLightbox.setOnSubmit(() => this.handleDatatransSubmit())
        this.datatransRedirect.watchResponseData((data) => this.handleDatatransResponseData(data))
      }
    },
    async performSubmitBillingDetails () {
      const { error } = await this.handlers.submitBillingDetailsForm()
      if (error) {
        EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
          type: 'error',
          alert: {
            message: error.response ? error.response?.data.message ?? error.message : this.$t('errors.failedToUpdateAccountInfo')
          }
        })
      }
    }
  },
  beforeUnmount () {
    if (!_.isEmpty(this.datatransLightbox)) {
      this.datatransLightbox.removeMessageListener()
    }
  },
  mounted () {
    // Register Datatrans callback
    this.initDatatransLightbox()
  }
}
